// @ts-nocheck
// app/courses/[slug].tsx — Editorial dynamic category detail

import Head from 'expo-router/head';
import { useCallback, useMemo } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { ArrowLeft, AlertTriangle, Check } from 'lucide-react-native';

import { theme, ACCENT, CRIMSON, FOREST, INK, INK_FAINT, INK_MUTED, INK_SOFT, PAPER, PAPER_SOFT } from '@/constants/theme';
import {
  DoubleRule, EditorialButton, EditorialFooter, Eyebrow, Headline, Numeral, Paper, PageHero, Section as ES, Tag, Title,
  useEditorialBreakpoint,
} from '@/components/editorial';
import { EditorialHeader } from '@/components/editorial/EditorialHeader';
import { AccessibilityButton } from '@/components/AccessibilityButton';

type Slug = 'stock-market'|'crypto'|'programming'|'devops-cloud'|'cyber'|'qa'|'digital-marketing'|'sales'|'product'|'ui-ux'|'data-ai'|'automation'|'finance'|'english'|'psychometric'|'academic';

type PageData = {
  slug: Slug; title: string; subtitle: string; badge?: string; tags: string[]; bullets: string[];
  whoFor: string[]; whoNotFor: string[]; checklist: string[]; redFlags: string[]; whatYouGet: string[]; questionsToAsk: string[];
  chatSeed: string; seoDescription: string;
};

const SERIF = theme.fonts.serif;
const SERIF_BLACK = theme.fonts.serifBlack;
const SERIF_BOLD = theme.fonts.serifBold;
const SANS = theme.fonts.regular;
const SANS_BOLD = theme.fonts.bold;
const MONO = theme.fonts.mono;
const IS_WEB = Platform.OS === 'web';

const DATA: Record<Slug, PageData> = {
  'stock-market': { slug: 'stock-market', title: 'שוק ההון', badge: 'פופולרי', tags: ['מתחילים','פרקטיקה','קריירה'], subtitle: 'מסחר/השקעות וניהול סיכונים — לבחור מסלול שעובד לפי כללים, לא לפי חלומות.', bullets: ['מה לבדוק לפני תשלום','ניהול סיכונים אמיתי','דגלים אדומים בשיווק'],
    whoFor: ['מי שאוהב כללים, מספרים, משמעת','מי שמוכן לתרגל ולהבין הפסדים','מי שמחפש תהליך ולא "מכה"'],
    whoNotFor: ['מי שרוצה כסף קל ומהיר','מי שלא מוכן לנהל סיכונים','מי לא מוכן לתרגל בלי להמר'],
    checklist: ['יש תכנית תרגול מסודרת?','יש דוגמאות לניהול סיכונים, סטופ, Risk/Reward?','האם מראים עסקאות מפורטות כולל הפסדים?','האם יש תמיכה אמיתית?','האם יש שקיפות לגבי עלויות וסיכונים?'],
    redFlags: ['הבטחות תשואה / "100% הצלחה"','תוכן שמדבר רק על רווחים','דוחפים "להיכנס מהר" בלי מסגרת','אין כללים ברורים, רק סיפורים'],
    whatYouGet: ['הבנה של סוגי שווקים ואסטרטגיות','מסגרת תרגול ויומן מסחר','כללים לניהול סיכונים','יכולת להבחין בין השקעה למסחר'],
    questionsToAsk: ['כמה שעות תרגול בשבוע?','איך נראית תכנית עבודה שבוע 1–4?','איזה יומן מסחר משתמשים?','מה עושים ברצף הפסדים?'],
    chatSeed: 'אני רוצה התאמה לקורס שוק ההון. אני מתחיל/ה. חשוב לי מסגרת תרגול וניהול סיכונים.', seoDescription: 'עמוד שוק ההון — צ׳ק־ליסט החלטה, דגלים אדומים, התאמה אישית בצ׳אט.' },
  crypto: { slug: 'crypto', title: 'קריפטו', badge: 'חם עכשיו', tags: ['מתחילים','סיכון','פרקטיקה'], subtitle: 'מסחר, ארנקים ובטיחות — להפריד בין ידע אמיתי לשיווק.', bullets: ['בטיחות וארנקים','ניהול סיכונים','איך לזהות שיווק אגרסיבי'],
    whoFor: ['מי שמבין שזה שוק תנודתי','מי שרוצה ללמוד בטיחות לפני מסחר','מי שמוכן להתייחס לזה כמיומנות'],
    whoNotFor: ['מי שמחפש כפולות קצרות','מי שלא מקפיד על אבטחה','מי לא מוכן לתרגל בלי כסף אמיתי'],
    checklist: ['יש פרק בטיחות: ארנקים, 2FA, פישינג?','האם מסבירים מושגים בסיסיים?','האם יש תכנית תרגול ולא רק סיגנלים?','האם יש שקיפות לגבי סיכונים?'],
    redFlags: ['קורס שמבוסס על סיגנלים בלבד','מינוף כפתרון קסם','אין חלק אבטחה','מבטיחים תשואה בטוחה'],
    whatYouGet: ['הבנה של תשתית קריפטו','בטיחות וניהול חשבון','כניסה הדרגתית למסחר','כללי מינוף וסיכון'],
    questionsToAsk: ['האם מלמדים בטיחות "ידיים על"?','כמה דגש על ניהול סיכונים?','מה עושים בתנודתיות קיצונית?'],
    chatSeed: 'אני רוצה התאמה לקורס קריפטו. אני מתחיל/ה. חשוב לי בטיחות, מסגרת תרגול וניהול סיכונים.', seoDescription: 'עמוד קריפטו — בטיחות, צ׳ק־ליסט, דגלים אדומים, התאמה בצ׳אט.' },
  programming: { slug: 'programming', title: 'תכנות', badge: 'קריירה', tags: ['קריירה','מתחילים','תיק עבודות'], subtitle: 'מסלולים למתחילים, פרויקטים ותיק עבודות — בלי הבטחות שווא.', bullets: ['בחירת מסלול לפי יעד','פרויקטים אמיתיים','איך לזהות קורס חלש'],
    whoFor: ['מי שמוכן להשקיע זמן עקבי','מי שמוכן להתמודד עם תסכול','מי שרוצה תיק עבודות'],
    whoNotFor: ['מי רוצה תוצאה בלי תרגול','מי מחפש קיצור דרך','מי לא יכול להקדיש זמן קבוע'],
    checklist: ['יש פרויקטים אמיתיים + משוב?','יש תכנית עבודה שבועית?','יש הכנה לראיונות וקורות חיים?','מה בדיוק הטכנולוגיות?'],
    redFlags: ['מבטיחים עבודה תוך X שבועות','אין תרגול אמיתי','אין בדיקה / קוד ריוויו','רק תעודה בלי תיק עבודות'],
    whatYouGet: ['יסודות שפה ופרקטיקה','פרויקטים לתיק עבודות','איך לומדים לבד אחרי הקורס','תהליך כניסה ריאלי לג׳וניור'],
    questionsToAsk: ['כמה פרויקטים מסיימים?','יש קוד ריוויו אישי?','מה עושים אם נתקעים?','איך נראה שבוע טיפוסי?'],
    chatSeed: 'אני רוצה התאמה לקורס תכנות למתחילים. חשוב לי פרויקטים ומשוב ותיק עבודות.', seoDescription: 'עמוד תכנות — צ׳ק־ליסט איכות, דגלים אדומים, התאמה אישית.' },
  'devops-cloud': { slug: 'devops-cloud', title: 'DevOps / Cloud', badge: 'קריירה', tags: ['קריירה','טכני','פרקטיקה'], subtitle: 'AWS/GCP, CI/CD ותפעול — מסלול שמייצר יכולת, לא סיסמאות.', bullets: ['CI/CD אמיתי','תשתיות בסיס','פרויקט ריל'],
    whoFor: ['מי שכבר עם בסיס טכני','מי שאוהב תשתיות ואוטומציה','מי שמוכן לבנות פרויקט תשתיתי'],
    whoNotFor: ['מי מתחיל לגמרי בלי בסיס','מי רוצה רק תעודה','מי לא מוכן לעבוד מעשית עם ענן'],
    checklist: ['יש מעבדה: CI/CD + IaC?','מלמדים לינוקס ורשתות שימושית?','דגש על אבטחה והרשאות?','מה הסטאק המדויק?'],
    redFlags: ['רק מצגות, בלי מעבדות','קופצים ל-K8s בלי יסודות','אין פרויקט סופי אמיתי'],
    whatYouGet: ['בסיס ענן + אוטומציה','הבנה של CI/CD מעשי','חשיבה תפעולית נכונה','פרויקט לתיק'],
    questionsToAsk: ['איזה פרויקט תסיימו?','האם יש תרגול שבועי?','מה דרישות קדם?'],
    chatSeed: 'אני רוצה התאמה לקורס DevOps/Cloud. תן 3 התאמות ותשאל שאלה אחת על ניסיון קודם.', seoDescription: 'עמוד DevOps/Cloud — צ׳ק־ליסט מסלול, דגלים אדומים, התאמה בצ׳אט.' },
  cyber: { slug: 'cyber', title: 'סייבר', badge: 'קריירה', tags: ['קריירה','טכני','מעבדות'], subtitle: 'הגנת מידע, רשתות ומעבדות — בלי אשליות של "תוך חודש".', bullets: ['מעבדות ותרגול','בסיס רשתות','מסלול ריאלי לג׳וניור'],
    whoFor: ['מי שאוהב חקירה ודיבאג','מי מוכן ללמוד יסודות לפני כלים','מי רוצה מעבדות אמיתיות'],
    whoNotFor: ['מי חושב שסייבר זה רק האקרים','מי לא מוכן לרשתות/לינוקס','מי מחפש תוצאה בלי תרגול'],
    checklist: ['יש מעבדות (CTF/סימולציות)?','מלמדים רשתות ולינוקס שימושי?','יש תרגול blue team / incident response?','מה מסלול ההכנה לג׳וניור?'],
    redFlags: ['מבטיחים תפקיד מהר','אין מעבדות, רק תיאוריה','תוכן שטחי על כלים'],
    whatYouGet: ['בסיס רשתות ולינוקס','מעבדות תרגול','שפה מקצועית וכלי עבודה','מסגרת כניסה ריאלית'],
    questionsToAsk: ['איזה מעבדות עושים?','האם יש פרויקט סייבר לתיק?','מה דרישות קדם אמיתיות?'],
    chatSeed: 'אני רוצה התאמה לקורס סייבר. חשוב לי מעבדות ותרגול.', seoDescription: 'עמוד סייבר — צ׳ק־ליסט, דגלים אדומים, התאמה בצ׳אט.' },
  qa: { slug: 'qa', title: 'QA / בדיקות', tags: ['קריירה','מתחילים','פרקטיקה'], subtitle: 'בדיקות ידניות/אוטומציה — מסלול כניסה טוב לטק אם בונים פרקטיקה.', bullets: ['Manual מול Automation','תרגול על מוצר אמיתי','תיק וקורות חיים'],
    whoFor: ['מי רוצה כניסה מסודרת לטק','מי אוהב סדר ותהליכים','מי מוכן לכתוב תרחישים ודוחות'],
    whoNotFor: ['מי שונא דקדקנות','מי רוצה רק לעבור קורס בלי פרקטיקה'],
    checklist: ['יש תרגול test cases ודוחות באגים?','יש תרגול על מערכת אמיתית?','אם יש אוטומציה — באיזה שפה?','יש פרויקט סופי / פורטפוליו?'],
    redFlags: ['קורס בלי תרגול','אוטומציה בלי בסיס ידני','תעודה בלי תוצרים'],
    whatYouGet: ['בסיס QA ידני','עקרונות בדיקות','התקדמות לאוטומציה','פרקטיקה לתיק'],
    questionsToAsk: ['כמה תרגול כתיבה יש?','יש סימולציה של עבודת צוות?','מה הסטאק באוטומציה?'],
    chatSeed: 'אני רוצה התאמה לקורס QA למתחילים. חשוב לי פרקטיקה ותיק עבודות.', seoDescription: 'עמוד QA — צ׳ק־ליסט, דגלים אדומים, התאמה בצ׳אט.' },
  'digital-marketing': { slug: 'digital-marketing', title: 'שיווק דיגיטלי', tags: ['קריירה','פרקטיקה','מדידה'], subtitle: 'קמפיינים, מדידה ותוצרים — להבין מה באמת לומדים.', bullets: ['PPC/SEO/סושיאל','GA4 בסיס','תוצרים: קמפיין + KPI'],
    whoFor: ['מי רוצה תוצאות עם תוצרים','מי מוכן למדוד ולהשתפר','מי מחבר קריאייטיב ודאטה'],
    whoNotFor: ['מי לא מוכן למדידה','מי רוצה רק תעודה'],
    checklist: ['יש תרגול בניהול קמפיינים?','מלמדים GA4/Events מעשי?','יש תיק: דוחות/קריאייטיב/קייס?','מה ההתמחות המדויקת?'],
    redFlags: ['קורס בלי תרגול אמיתי','אין דוחות/מדידה','רק "סודות" בלי מתודולוגיה'],
    whatYouGet: ['יכולת להרים קמפיין','בסיס מדידה','תוצרים לתיק','שפה מקצועית'],
    questionsToAsk: ['כמה קמפיינים עושים?','האם יש ביקורת על דוחות?','מה נדרש כדי לסיים?'],
    chatSeed: 'אני רוצה התאמה לקורס שיווק דיגיטלי. חשוב לי תרגול וקייסים ומדידה.', seoDescription: 'עמוד שיווק דיגיטלי — צ׳ק־ליסט, דגלים אדומים, התאמה בצ׳אט.' },
  sales: { slug: 'sales', title: 'מכירות', tags: ['קריירה','פרקטיקה','ליווי'], subtitle: 'BDR/SDR, סקריפטים ותרגול — להפוך ידע ליכולת בשטח.', bullets: ['תרגול שיחות','סקריפטים','תהליך עבודה'],
    whoFor: ['מי שמוכן לתרגל שיחות','מי רוצה מקצוע פרקטי עם KPI','מי פתוח לפידבק'],
    whoNotFor: ['מי נמנע מתקשורת','מי לא מוכן לתרגול'],
    checklist: ['יש role-play אמיתי?','יש תסריטים + פידבק?','מלמדים תהליך מלא לידים?','יש הכנה לראיונות?'],
    redFlags: ['תוכן תיאורטי בלי תרגול','אין פידבק אמיתי','אין מסגרת KPI'],
    whatYouGet: ['יכולת שיחה','סקריפטים','התמודדות עם התנגדויות','מסגרת עבודה'],
    questionsToAsk: ['כמה role-play עושים?','האם יש ניתוח שיחות?','מה רמת הליווי?'],
    chatSeed: 'אני רוצה התאמה לקורס מכירות (BDR/SDR). חשוב לי תרגול שיחות ופידבק.', seoDescription: 'עמוד מכירות — צ׳ק־ליסט, דגלים אדומים, התאמה בצ׳אט.' },
  product: { slug: 'product', title: 'ניהול מוצר', badge: 'מבוקש', tags: ['קריירה','תיק עבודות','פרקטיקה'], subtitle: 'מוצר, אסטרטגיה ומחקר — תוצרים, לא רק מושגים.', bullets: ['PRD + Roadmap','מחקר משתמשים','Case study אמיתי'],
    whoFor: ['מי שאוהב חשיבה מוצרית','מי מוכן לקייס סטאדי','מי רוצה להיכנס לעולם מוצר'],
    whoNotFor: ['מי לא מוכן לכתוב תוצרים','מי מחפש קיצור דרך'],
    checklist: ['יש Case study מלא?','יש PRD כתוב + פידבק?','יש Analytics בסיסי?','יש user research מעשי?'],
    redFlags: ['קורס בלי תוצרים','רק מצגות','אין פידבק על עבודות'],
    whatYouGet: ['קייס סטאדי','PRD ומסמכים','חשיבה על מדדים','שפה מקצועית'],
    questionsToAsk: ['איך נראה הקייס?','יש ביקורת אמיתית?','מה דרישות קדם?'],
    chatSeed: 'אני רוצה התאמה לקורס ניהול מוצר. חשוב לי קייס סטאדי ותוצרים.', seoDescription: 'עמוד ניהול מוצר — צ׳ק־ליסט, דגלים אדומים, התאמה בצ׳אט.' },
  'ui-ux': { slug: 'ui-ux', title: 'UI / UX', tags: ['קריירה','תיק עבודות','פרקטיקה'], subtitle: 'עיצוב מוצר ותיק עבודות — תוצרים, לא סיסמאות.', bullets: ['קייס סטאדי','Figma','משוב אמיתי'],
    whoFor: ['מי שרוצה תיק עבודות','מי פתוח לפידבק','מי מוכן להשקיע בקייסים'],
    whoNotFor: ['מי לא מוכן לתיק עבודות','מי רוצה לסיים מהר'],
    checklist: ['כמה קייסים מסיימים?','יש משוב 1:1?','יש תהליך מלא: מחקר→UI→פרוטוטייפ?','מתייחסים למוצר, לא רק לעיצוב?'],
    redFlags: ['קורס בלי קייסים','אין משוב','רק טריקים בלי תהליך'],
    whatYouGet: ['קייס סטאדי','Figma ופרוטוטייפ','שפה מוצרית','תיק רלוונטי'],
    questionsToAsk: ['כמה ביקורות תקבל?','האם יש דוגמאות בוגרים?','מה קצב עבודה שבועי?'],
    chatSeed: 'אני רוצה התאמה לקורס UI/UX. חשוב לי קייס סטאדי ותיק עבודות.', seoDescription: 'עמוד UI/UX — צ׳ק־ליסט, דגלים אדומים, התאמה בצ׳אט.' },
  'data-ai': { slug: 'data-ai', title: 'דאטה / AI', badge: 'קריירה', tags: ['קריירה','טכני','תיק עבודות'], subtitle: 'אנליטיקה, SQL, Python ופרויקטים — מסלול ברור.', bullets: ['SQL + Python','דאטה אמיתי','פרויקט לתיק'],
    whoFor: ['מי שאוהב ניתוח','מי מוכן לתרגל','מי רוצה פרויקט מדיד'],
    whoNotFor: ['מי לא מוכן למתמטיקה בסיסית','מי מחפש תוצאות בלי תרגול'],
    checklist: ['יש פרויקט עם דאטה אמיתי?','SQL לעומק או רק בסיס?','Python לתרגול או היכרות?','BI / DS / ML — ההבדל ברור?'],
    redFlags: ['קורס בלי פרויקט','מכנים AI בלי תוכן','אין תרגול SQL'],
    whatYouGet: ['SQL שימושי','Python מעשי','פרויקט לתיק','יכולת להסביר מסקנות'],
    questionsToAsk: ['איזה פרויקט מסיימים?','כמה תרגול שבועי?','האם יש ביקורת על קוד?'],
    chatSeed: 'אני רוצה התאמה לקורס דאטה/AI. חשוב לי SQL, פרויקט אמיתי ותיק עבודות.', seoDescription: 'עמוד דאטה/AI — צ׳ק־ליסט, דגלים אדומים, התאמה בצ׳אט.' },
  automation: { slug: 'automation', title: 'אוטומציה / No-Code', tags: ['פרקטיקה','עסקים','תהליכים'], subtitle: 'n8n, Zapier, Airtable — תהליכים שמביאים תוצאה.', bullets: ['תהליכים אמיתיים','אינטגרציות','שיטות עבודה נקיות'],
    whoFor: ['מי רוצה אוטומציות לעסק','מי אוהב סדר ותהליכים','מי רוצה ROI מהיר'],
    whoNotFor: ['מי לא מוכן לתחזק תהליך','מי עושה חיבורים בלי תיעוד'],
    checklist: ['מלמדים best practices (לוגים, retries)?','יש תרגול Leads/CRM/Sheets?','מלמדים אבטחה (tokens)?','יש פרויקט סופי אמיתי?'],
    redFlags: ['מראים רק דמו','אין תיעוד','לא מדברים על שגיאות'],
    whatYouGet: ['יכולת לבנות flows','אינטגרציות שימושיות','שיטות עבודה','פרויקט להראות'],
    questionsToAsk: ['איזה תהליכים בונים?','איך מטפלים בשגיאות?','האם יש תבניות?'],
    chatSeed: 'אני רוצה התאמה לקורס אוטומציה/No-Code (n8n). חשוב לי פרקטיקה.', seoDescription: 'עמוד אוטומציה — צ׳ק־ליסט, דגלים אדומים, התאמה בצ׳אט.' },
  finance: { slug: 'finance', title: 'פיננסים / הנהלת חשבונות', tags: ['קריירה','מתחילים','פרקטיקה'], subtitle: 'יסודות, ניתוחים וכלים — מסלול פרקטי לשוק.', bullets: ['מה לומדים בפועל','כלים','למי זה רלוונטי'],
    whoFor: ['מי רוצה מקצוע פרקטי','מי אוהב מספרים','מי רוצה מסלול ברור'],
    whoNotFor: ['מי לא אוהב עבודה מדויקת','מי מחפש "מהיר בלי מאמץ"'],
    checklist: ['יש תרגול על תוכנות?','יש תרגילים עם דוחות אמיתיים?','מה ההסמכה?','מה אפשר לעשות בסוף?'],
    redFlags: ['תיאור מעורפל','אין תרגול','מבטיחים תפקיד בלי תנאים'],
    whatYouGet: ['יסודות פיננסים','כלים ותהליכים','תרגול דוחות','מסלול עבודה'],
    questionsToAsk: ['מה עושים בסוף?','כמה תרגול מעשי?','איזה כלים מלמדים?'],
    chatSeed: 'אני רוצה התאמה לקורס פיננסים/הנהלת חשבונות.', seoDescription: 'עמוד פיננסים — צ׳ק־ליסט, דגלים אדומים, התאמה בצ׳אט.' },
  english: { slug: 'english', title: 'אנגלית מקצועית', tags: ['מתחילים','קריירה','שיפור'], subtitle: 'דיבור, מיילים, ראיונות — שיפור מדיד, לא תחושה.', bullets: ['תרגול דיבור','מיילים/ראיונות','שיפור מדיד'],
    whoFor: ['מי צריך אנגלית לעבודה','מי רוצה ראיונות טובים','מי מוכן לתרגל בקול'],
    whoNotFor: ['מי מחפש תיאוריה בלבד','מי לא מוכן להתאמן'],
    checklist: ['יש דיבור בפועל?','יש סימולציות ראיונות?','יש תיקון כתיבה?','יש מדידה והתקדמות?'],
    redFlags: ['רק שיעורים בלי דיבור','אין תרגול','אין מדידה'],
    whatYouGet: ['שיפור דיבור','כתיבה מקצועית','ביטחון בראיונות','מסגרת תרגול'],
    questionsToAsk: ['כמה דיבור בפועל?','איך מודדים התקדמות?','מותאם לתחום עבודה?'],
    chatSeed: 'אני רוצה התאמה לקורס אנגלית מקצועית. חשוב לי דיבור וראיונות.', seoDescription: 'עמוד אנגלית מקצועית — צ׳ק־ליסט, דגלים אדומים, התאמה בצ׳אט.' },
  psychometric: { slug: 'psychometric', title: 'פסיכומטרי / מכינות', tags: ['אקדמיה','משמעת','שיפור'], subtitle: 'שגרה, סימולציות ומעקב — המסגרת שמייצרת תוצאות.', bullets: ['תכנית שבועית','סימולציות','ניתוח טעויות'],
    whoFor: ['מי צריך מסגרת','מי מוכן לתרגל הרבה','מי רוצה מעקב'],
    whoNotFor: ['מי לא מוכן לשגרה','מי מחפש פתרון קסם'],
    checklist: ['כמה סימולציות?','יש ניתוח טעויות מסודר?','יש תכנית שבועית?','יש התאמה אישית?'],
    redFlags: ['אין סימולציות','אין ניתוח טעויות','אין תכנית שבועית'],
    whatYouGet: ['מסגרת תרגול','מדידה והתקדמות','ניתוח טעויות','תכנית עבודה'],
    questionsToAsk: ['כמה סימולציות?','איך עושים ניתוח טעויות?','מה התמיכה בין שיעורים?'],
    chatSeed: 'אני רוצה התאמה למסגרת פסיכומטרי. חשוב לי תכנית שבועית וסימולציות.', seoDescription: 'עמוד פסיכומטרי — צ׳ק־ליסט, דגלים אדומים, התאמה בצ׳אט.' },
  academic: { slug: 'academic', title: 'מסלולים אקדמיים', badge: 'חדש', tags: ['אקדמיה','השוואה','תכנון'], subtitle: 'תואר, מכינה או קורס? להבין מה הדרך הנכונה.', bullets: ['מסלול לפי יעד','זמן/עלות/תמורה','איפה נופלים'],
    whoFor: ['מי מתלבט בין תואר/קורס','מי רוצה תכנון מסודר','מי רוצה החלטה ריאלית'],
    whoNotFor: ['מי מחפש תשובה בלי פרטים','מי לא מוכן לזמן ועלות'],
    checklist: ['מה היעד בסוף?','כמה זמן פנוי בשבוע?','תקציב מקסימום?','דרישות קדם?'],
    redFlags: ['מבטיחים מסלול בטוח','אין שקיפות זמן/עלות','אין תכנית ריאלית'],
    whatYouGet: ['החלטה מסודרת','מסלול ריאלי','אומדן זמן/עלות','צעד הבא ברור'],
    questionsToAsk: ['מה היעד התעסוקתי?','מה הדדליין?','האם יש דרישות קדם?','מה התקציב?'],
    chatSeed: 'אני רוצה מסלול אקדמי שמתאים לי. תשאל אותי 3 שאלות קצרות.', seoDescription: 'עמוד מסלולים אקדמיים — צ׳ק־ליסט החלטה, התאמה בצ׳אט.' },
};

export default function SlugPage() {
  const router = useRouter();
  const params = useLocalSearchParams();
  const { isDesktop } = useEditorialBreakpoint();
  const slug = ((params.slug ?? '') as string) as Slug;
  const data = (DATA as any)[slug] as PageData | undefined;

  const goToChat = useCallback(() => {
    if (!data) return;
    router.push({ pathname: '/chat', params: { initialMessage: data.chatSeed, skipWelcome: 'true' } });
  }, [router, data]);

  const courseJsonLd = useMemo(() => {
    if (!data) return null;
    return {
      '@context': 'https://schema.org', '@type': 'Course',
      name: `קורסי ${data.title} בישראל — מדריך השוואה`,
      description: data.seoDescription,
      url: `https://kursiaii.com/courses/${data.slug}`,
      inLanguage: 'he',
      provider: { '@type': 'Organization', name: 'Kursi', url: 'https://kursiaii.com' },
      teaches: data.tags,
      courseMode: ['online', 'onsite'],
      educationalLevel: 'Beginner to Advanced',
    };
  }, [data]);

  if (!data) {
    return (
      <Paper>
        <Stack.Screen options={{ title: 'לא נמצא' }} />
        <EditorialHeader />
        <ES py="xxl" px="md" max={800}>
          <View style={{ alignItems: 'center' }}>
            <Eyebrow color={CRIMSON}>קטגוריה לא קיימת</Eyebrow>
            <View style={{ height: 14 }} />
            <Headline level={1} align="center">העמוד לא נמצא</Headline>
            <View style={{ height: 14 }} />
            <Text style={s.body}>נראה שהלינק לא תואם לקטגוריה קיימת. חזרו לקטגוריות ובחרו מחדש.</Text>
            <View style={{ height: 22 }} />
            <View style={{ flexDirection: 'row-reverse', gap: 14 } as any}>
              <EditorialButton label="חזרה לקטגוריות" onPress={() => router.push('/courses')} variant="primary" />
              <EditorialButton label="דף הבית" onPress={() => router.push('/')} variant="secondary" />
            </View>
          </View>
        </ES>
      </Paper>
    );
  }

  return (
    <Paper>
      <AccessibilityButton />
      <Stack.Screen options={{ title: data.title }} />
      {IS_WEB && (
        <Head>
          <title>{data.title} · Kursi</title>
          <meta name="description" content={data.seoDescription} />
          <link rel="canonical" href={`https://kursiaii.com/courses/${data.slug}`} />
          {courseJsonLd && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(courseJsonLd) }} />}
        </Head>
      )}

      <ScrollView style={{ flex: 1, backgroundColor: PAPER }} contentContainerStyle={{ backgroundColor: PAPER }}>
        <EditorialHeader />

        <ES py="xl" px="md" max={1100}>
          <PageHero
            kicker={`גיליון ${data.title} · עיתון Kursi`}
            title={<>{data.title}</>}
            lede={data.subtitle}
            meta={data.tags.map(t => t.toUpperCase()).join(' · ')}
          />
          <View style={{ flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 12, marginTop: 18 } as any}>
            <EditorialButton label="בדיקת התאמה בצ׳אט" onPress={goToChat} variant="primary" size="lg" trailing={<ArrowLeft size={16} color={PAPER} />} />
            <EditorialButton label="שאלון התאמה" onPress={() => router.push('/quiz')} variant="secondary" size="lg" />
          </View>
        </ES>

        {/* Bullets row */}
        <ES py="md" px="md" max={1100}>
          <View style={s.bulletsRow}>
            {data.bullets.map((b, i) => (
              <View key={i} style={[s.bulletCol, i > 0 && { borderRightWidth: 1, borderRightColor: INK }]}>
                <Numeral size={48} color={ACCENT}>{`0${i + 1}`}</Numeral>
                <View style={{ height: 10 }} />
                <Text style={s.bulletColText}>{b}</Text>
              </View>
            ))}
          </View>
        </ES>

        {/* Two-column: who-for vs who-not-for */}
        <ES py="xl" px="md" max={1100}>
          <View style={s.twoCol}>
            <View style={s.colHalf}>
              <Eyebrow color={FOREST}>למי זה מתאים</Eyebrow>
              <View style={{ height: 14 }} />
              <Title level={2}>זה בשבילכם אם…</Title>
              <View style={{ height: 16 }} />
              {data.whoFor.map((x, i) => (
                <View key={`y${i}`} style={s.rowMark}>
                  <Check size={16} color={FOREST} />
                  <Text style={s.rowText}>{x}</Text>
                </View>
              ))}
            </View>
            <View style={s.colHalf}>
              <Eyebrow color={CRIMSON}>למי זה פחות מתאים</Eyebrow>
              <View style={{ height: 14 }} />
              <Title level={2}>תחשבו שוב אם…</Title>
              <View style={{ height: 16 }} />
              {data.whoNotFor.map((x, i) => (
                <View key={`n${i}`} style={s.rowMark}>
                  <AlertTriangle size={14} color={CRIMSON} />
                  <Text style={s.rowText}>{x}</Text>
                </View>
              ))}
            </View>
          </View>
        </ES>

        <ES py="md" px="md" max={1100}><DoubleRule /></ES>

        {/* Checklist + redflags + whatyouget + questions */}
        <ES py="xl" px="md" max={1100}>
          {[
            { title: 'צ׳ק־ליסט לפני הרשמה', items: data.checklist, kicker: 'ההוצאה הראשונה', kColor: ACCENT, mark: '✓' },
            { title: 'דגלים אדומים', items: data.redFlags, kicker: 'אזהרת עורך', kColor: CRIMSON, mark: '×' },
            { title: 'מה תקבלו בסוף', items: data.whatYouGet, kicker: 'התוצר', kColor: FOREST, mark: '—' },
            { title: 'שאלות לשאול לפני שמשלמים', items: data.questionsToAsk, kicker: 'בדיקת איכות', kColor: INK, mark: '?' },
          ].map((sec, idx) => (
            <View key={idx} style={s.sectionBlock}>
              <Eyebrow color={sec.kColor}>{sec.kicker}</Eyebrow>
              <View style={{ height: 14 }} />
              <Title level={2}>{sec.title}</Title>
              <View style={{ height: 18 }} />
              {sec.items.map((it, i) => (
                <View key={i} style={s.checkItem}>
                  <Text style={[s.checkMark, { color: sec.kColor }]}>{sec.mark}</Text>
                  <Text style={s.checkText}>{it}</Text>
                </View>
              ))}
            </View>
          ))}
        </ES>

        {/* Final CTA */}
        <ES py="xxl" px="md" max={1100}>
          <View style={s.finalCta}>
            <Headline level={2} color={PAPER}>רוצים שנדייק לכם?</Headline>
            <View style={{ height: 14 }} />
            <Text style={[s.body, { color: PAPER_SOFT, maxWidth: 540 }]}>
              שורת חיפוש אחת. שלוש התאמות. אם חסר פרט קריטי — נשאל שאלה אחת לפני שניתן תשובה.
            </Text>
            <View style={{ height: 24 }} />
            <EditorialButton label="המשך להתאמה בצ׳אט" onPress={goToChat} variant="accent" size="lg" trailing={<ArrowLeft size={16} color={PAPER} />} />
          </View>
        </ES>

        <EditorialFooter onNavigate={(href) => router.push(href as any)} />
      </ScrollView>
    </Paper>
  );
}

const s = StyleSheet.create({
  body: { fontFamily: SANS, fontSize: 17, lineHeight: 28, color: INK_SOFT, textAlign: 'right' },

  bulletsRow: { flexDirection: 'row-reverse', borderWidth: 1, borderColor: INK, backgroundColor: PAPER_SOFT, flexWrap: 'wrap' } as any,
  bulletCol: { flex: 1, padding: 28, minWidth: 240, alignItems: 'flex-end' },
  bulletColText: { fontFamily: SERIF_BOLD, fontSize: 19, color: INK, letterSpacing: -0.3, textAlign: 'right' },

  twoCol: { flexDirection: IS_WEB ? 'row-reverse' : 'column', gap: 0 } as any,
  colHalf: { flex: 1, padding: 28, borderWidth: 1, borderColor: INK, backgroundColor: PAPER_SOFT, marginLeft: -1 },
  rowMark: { flexDirection: 'row-reverse', gap: 12, alignItems: 'flex-start', paddingVertical: 8 } as any,
  rowText: { flex: 1, fontFamily: SANS, fontSize: 16, lineHeight: 26, color: INK, textAlign: 'right' },

  sectionBlock: { paddingVertical: 28, borderTopWidth: 1, borderTopColor: INK },
  checkItem: { flexDirection: 'row-reverse', gap: 14, paddingVertical: 8 } as any,
  checkMark: { fontFamily: SERIF_BLACK, fontSize: 22, width: 26, textAlign: 'center', lineHeight: 24 },
  checkText: { flex: 1, fontFamily: SANS, fontSize: 16, lineHeight: 26, color: INK, textAlign: 'right' },

  finalCta: { backgroundColor: INK, padding: 48, alignItems: 'flex-start' },
});
